import { useState, type ReactNode } from 'react';
import { useLocation } from 'react-router';
import { AdjustmentsReportsNav, isAdjustmentsReportsRoute } from './AdjustmentsReportsNav';
import { ComplianceFilingNav, isComplianceFilingRoute } from './ComplianceFilingNav';
import { ManagementReportsNav, isManagementReportsRoute } from './ManagementReportsNav';
import { ReportSettingsNav, isReportSettingsRoute } from './ReportSettingsNav';
import { RevisedAccountsNav, isRevisedAccountsRoute } from './RevisedAccountsNav';
import { IntegrationMenu, LogsMenu, ReportsMenu, SettingsMenu, TaskMenu } from './menus';

export type SideMenuKind = 'task' | 'settings' | 'reports' | 'integration' | 'logs';

const MENUS = {
  task: TaskMenu,
  settings: SettingsMenu,
  reports: ReportsMenu,
  integration: IntegrationMenu,
  logs: LogsMenu,
};

interface SideMenuPageProps {
  menu: SideMenuKind;
  /**
   * Angular pages listened to the menu's toggle and flipped `[ngClass]="{'hide-sidelist': istoggleSideList}"` on
   * their content. Some pages have an empty handler: their content never moves. Pass false for those.
   */
  syncContent?: boolean;
  /** Extra classes on the content `.form` element. */
  contentClassName?: string;
  children: ReactNode;
}

/**
 * The frame of every per-client page: the left menu plus the content pane, wired the way the Angular pages wired them
 * (`<div class="sidemenu-hide"><div class="d-flex h-100"><app-*-menu/><div class="form wp-100">`).
 */
export function SideMenuPage({ menu, syncContent = true, contentClassName = '', children }: SideMenuPageProps) {
  const [closeMenu, setCloseMenu] = useState(true);
  const Menu = MENUS[menu];
  const hideContent = syncContent ? closeMenu : true;
  // APT348: the four Adjustments & Reports screens share one navigation bar across the top of their content.
  const { pathname } = useLocation();
  const showAdjustmentsNav = menu === 'task' && isAdjustmentsReportsRoute(pathname);
  // APT353: so do the four Compliance & Filing screens.
  const showComplianceNav = menu === 'task' && isComplianceFilingRoute(pathname);
  // APT370: and the two Revised Accounts screens, under Settings.
  const showRevisedNav = menu === 'settings' && isRevisedAccountsRoute(pathname);
  // APT371: the pages of Report Settings, under Settings.
  const showReportSettingsNav = menu === 'settings' && isReportSettingsRoute(pathname);
  // APT375: and the Management Reports screens, under Reports.
  const showManagementNav = menu === 'reports' && isManagementReportsRoute(pathname);

  return (
    <div className="sidemenu-hide">
      <div className="d-flex h-100">
        <Menu
          closeMenu={closeMenu}
          onToggle={() => {
            setCloseMenu((closed) => !closed);
            window.dispatchEvent(new Event('resize'));
          }}
        />
        <div className={`form wp-100 ${hideContent ? 'hide-sidelist' : ''} ${contentClassName}`.trim()}>
          {showAdjustmentsNav && <AdjustmentsReportsNav />}
          {showComplianceNav && <ComplianceFilingNav />}
          {showRevisedNav && <RevisedAccountsNav />}
          {showReportSettingsNav && <ReportSettingsNav />}
          {showManagementNav && <ManagementReportsNav />}
          {children}
        </div>
      </div>
    </div>
  );
}
